import { CombinedGraphQLErrors } from '@apollo/client';
import { getClient } from '@api/client';
import { fragment } from '@api/fragment';
import { graphql } from '@api/generated';
import { ServiceUserUtils } from '../../lib';
import ServiceUser from '../../types/ServiceUser';
import {
  serviceUserFieldsFragment,
  serviceUserStatsFragment,
} from './serviceUserFragment';

const getServiceUserByCcUserIdQuery = graphql(`
  query getServiceUserByCcUserIdQuery(
    $contactCenterUserId: ID!
    $skipStats: Boolean = true
  ) {
    userByContactCenterUserId(contactCenterUserId: $contactCenterUserId) {
      ...ServiceUserFields
      ...ServiceUserStats @skip(if: $skipStats)
    }
  }
`);

/**
 * Fetch a single user by their `contactCenterUserId` (the id carried by RTCS
 * agent events). Returns null when no user matches. Stats-only GraphQL errors
 * are tolerated so the base record stays usable; other errors propagate.
 * Pass `fetchStats=true` to include availability/activity status and stats.
 */
async function getUserByCcUserId(
  contactCenterUserId: string,
  fetchStats = false,
): Promise<ServiceUser | null> {
  const { data, error } = await getClient().query({
    query: getServiceUserByCcUserIdQuery,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: { contactCenterUserId, skipStats: !fetchStats },
  });

  // Tolerate stats-only GraphQL errors so the user record is still usable; propagate others.
  // For this single-item query the path shape is ['userByContactCenterUserId', 'stats', ...],
  // so a nested stats field error (e.g. last segment 'lastActionAt') still belongs to stats.
  // Apollo v4 surfaces network/protocol failures as distinct error classes
  // (ServerError, ServerParseError, LinkError, …) — they are never combined
  // into CombinedGraphQLErrors, so the if/else discriminator covers every
  // propagation path.
  if (error) {
    if (CombinedGraphQLErrors.is(error)) {
      const nonStatusErrors = error.errors.filter(
        (e) => !e.path || e.path[1] !== 'stats',
      );
      if (nonStatusErrors.length > 0) {
        throw new Error(nonStatusErrors[0].message ?? error.message);
      }
    } else {
      throw new Error(error.message);
    }
  }

  const user = data?.userByContactCenterUserId;
  if (!user) return null;

  const base = fragment(serviceUserFieldsFragment, user);
  const statsFragment = fragment(serviceUserStatsFragment, user);

  const statusData =
    fetchStats && statsFragment
      ? ServiceUserUtils.parseAvailabilityActivityStatus(statsFragment)
      : undefined;

  return {
    ...base,
    stats: statsFragment?.stats ?? null,
    ...statusData,
  } as unknown as ServiceUser;
}

export default getUserByCcUserId;
